'use client';

import React, { ReactNode } from 'react';

import { useTranslations } from 'next-intl';

import { useConvert } from '@/api/trading/useConvert';
import { Container } from '@/blocks/components/Container';
import { AccentFragment } from '@/blocks/components/HeadingAccentFragment';
import { FormatDate } from '@/blocks/formatters/format-date';
import { useFormatter } from '@/blocks/formatters/format-number';
import { FaqBlockWithHeader } from '@/blocks/sections/Questions';
import { isFiniteNumber } from '@/utils/number';

import { ConverterCurrency } from '@/app/[lang]/converters/types';

import styles from '../../../sections/ConvertersHomeFaq/styles.module.scss';

type CurrencyFaqSectionProps = {
  currencyFrom: ConverterCurrency;
  currencyTo: ConverterCurrency;
  initialRate?: number;
  initialExampleRate?: number;
};

export function ConvertersFaq(props: CurrencyFaqSectionProps) {
  const tr = useTranslations();

  const { rate } = useConvert({
    from: props.currencyFrom.title,
    to: props.currencyTo.title,
    baseAmount: 1,
    fallbackData: props.initialRate ? { rate: props.initialRate } : undefined,
  });

  const { rate: exampleRate } = useConvert({
    from: 'BTC',
    to: 'USD',
    baseAmount: 1,
    fallbackData: props.initialExampleRate ? { rate: props.initialExampleRate } : undefined,
  });

  const formatter = useFormatter({
    maximumFractionDigits: 20,
  });

  const faqData: { question: ReactNode; answer: ReactNode }[] = [
    {
      question: tr.rich('pages.converters.symbol.sections.faq.cards.0.question', {
        value1: (_) => props.currencyFrom.description,
        value2: (_) => props.currencyTo.description,
      }),
      answer: tr.rich('pages.converters.symbol.sections.faq.cards.0.answer', {
        value1: (_) => props.currencyFrom.title,
        value2: (_) => props.currencyTo.title,
      }),
    },
    {
      question: tr.rich('pages.converters.symbol.sections.faq.cards.1.question', {
        value1: (_) => props.currencyFrom.title,
        value2: (_) => props.currencyTo.title,
      }),
      answer: tr.rich('pages.converters.symbol.sections.faq.cards.1.answer', {
        value1: (_) => props.currencyFrom.description,
        value2: (_) => props.currencyTo.description,
      }),
    },
    {
      question: tr.rich('pages.converters.symbol.sections.faq.cards.2.question', {
        value1: (_) => props.currencyFrom.description,
        value2: (_) => props.currencyTo.description,
      }),
      answer: tr.rich('pages.converters.symbol.sections.faq.cards.2.answer', {
        value1: (_) => props.currencyFrom.title,
        value2: (_) => props.currencyTo.title,
        date: (_) => (
          <FormatDate
            date={Date.now()}
            options={{
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }}
          />
        ),
        currentRate: (_) => formatter.format([rate, props.initialRate, 0].find(isFiniteNumber) as number),
      }),
    },
  ];

  return (
    <Container>
      <FaqBlockWithHeader
        className={styles.wrapper}
        title={tr.rich('pages.converters.symbol.sections.faq.title', {
          span: (content) => <AccentFragment>{content}</AccentFragment>,
        })}
        faqData={faqData}
      />
    </Container>
  );
}
